import React, { useState, useEffect, useContext, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaUpload, FaSave, FaSignOutAlt, FaLock, FaUserEdit, FaEye, FaEyeSlash } from 'react-icons/fa';
import { FiSettings } from 'react-icons/fi';
import { getRestaurantProfile, updateRestaurantProfile, uploadRestaurantLogo } from '../utils/api';
import AuthContext from '../contexts/AuthContext';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_API_BASE_URL_IMG 

function getImageUrl(path) {
  if (!path) return null;
  if (path.startsWith("http://") || path.startsWith("https://")) return path;
  const base = (API_URL || "").replace(/\/$/, "");
  const p = path.replace(/^\//, "");
  return `${base}/${p}`;
}

export default function Settings() {
  const { restaurantId } = useContext(AuthContext);
  const { logout } = useAuth();
  const navigate = useNavigate();
  
  const [profile, setProfile] = useState({ 
    name: '', 
    address: '', 
    phone: '', 
    logoUrl: '' 
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [activeTab, setActiveTab] = useState('profile');
  const fileInputRef = useRef(null);

  // Password update states
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [passwordError, setPasswordError] = useState('');

  const { updatePassword } = useAuth();

  useEffect(() => {
    if (restaurantId) {
      getRestaurantProfile(restaurantId)
        .then(data => {
          setProfile(data);
          setLoading(false);
        })
        .catch(err => {
          console.error("Failed to load profile", err);
          setError('Failed to load profile data');
          setLoading(false);
        });
    }
  }, [restaurantId]);

  const handleFieldChange = (field, value) => {
    setProfile(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      await updateRestaurantProfile({ restaurantId, ...profile });
      setSuccess('Profile updated successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to save changes. Please try again.');
      console.error("Save failed", err);
    }
    setSaving(false);
  };

  const handleLogoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setUploading(true);
    setError('');
    setSuccess('');
    try {
      const res = await uploadRestaurantLogo(file);
      setProfile(prev => ({ ...prev, logoUrl: res.logoUrl }));
      setSuccess('Logo updated successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Logo upload failed. Max 2MB allowed.');
      console.error('Upload failed', err);
    }
    setUploading(false);
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (err) {
      console.error('Logout failed:', err);
    }
  };

  // Real-time password update handler
  const handlePasswordUpdate = async (e) => {
    e.preventDefault();
    setPasswordLoading(true);
    setPasswordError('');
    setPasswordSuccess('');
    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordError('All fields are required.');
      setPasswordLoading(false);
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError('New passwords do not match.');
      setPasswordLoading(false);
      return;
    }
    try {
      await updatePassword(currentPassword, newPassword);
      setPasswordSuccess('Password updated successfully!');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setTimeout(() => setPasswordSuccess(''), 3000);
    } catch (err) {
      setPasswordError(err.message || 'Failed to update password.');
    }
    setPasswordLoading(false);
  };

  if (loading) return (
    <div className="flex items-center justify-center h-screen">
      <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-rose-500"></div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 to-amber-50 p-3 sm:p-4 md:p-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 sm:mb-8 gap-4">
          <div className="flex items-center space-x-2 sm:space-x-3">
            <FiSettings className="text-2xl sm:text-3xl text-rose-500" />
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-800">Restaurant Settings</h1>
          </div>
          <button 
            onClick={handleLogout}
            className="flex items-center space-x-2 text-gray-600 hover:text-rose-500 transition-colors text-sm sm:text-base"
          >
            <FaSignOutAlt />
            <span>Logout</span>
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 mb-6 sm:mb-8 overflow-x-auto">
          <button
            onClick={() => setActiveTab('profile')}
            className={`px-3 sm:px-4 py-2 font-medium text-sm sm:text-base whitespace-nowrap ${activeTab === 'profile' ? 'text-rose-500 border-b-2 border-rose-500' : 'text-gray-500 hover:text-gray-700'}`}
          >
            <div className="flex items-center space-x-1 sm:space-x-2">
              <FaUserEdit />
              <span>Profile</span>
            </div>
          </button>
          <button
            onClick={() => setActiveTab('security')}
            className={`px-3 sm:px-4 py-2 font-medium text-sm sm:text-base whitespace-nowrap ${activeTab === 'security' ? 'text-rose-500 border-b-2 border-rose-500' : 'text-gray-500 hover:text-gray-700'}`}
          >
            <div className="flex items-center space-x-1 sm:space-x-2">
              <FaLock />
              <span>Security</span>
            </div>
          </button>
        </div>

        {/* Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 sm:gap-8">
          {/* Profile Card */}
          <div className="lg:col-span-2">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3 }}
              className="bg-white rounded-xl shadow-sm p-4 sm:p-6"
            >
              {activeTab === 'profile' ? (
                <>
                  <h2 className="text-lg sm:text-xl font-semibold text-gray-800 mb-4 sm:mb-6">Restaurant Information</h2>
                  
                  <div className="flex flex-col lg:flex-row gap-6 sm:gap-8">
                    {/* Logo Upload */}
                    <div className="flex-shrink-0 flex justify-center lg:justify-start">
                      <div className="relative group">
                        <div className="w-32 h-32 sm:w-40 sm:h-40 rounded-xl overflow-hidden border-2 border-gray-100 shadow-sm">
                          <img 
                            src={profile.logoUrl ? getImageUrl(profile.logoUrl) : 'https://via.placeholder.com/160?text=Upload+Logo'} 
                            alt="Restaurant Logo" 
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <button 
                          onClick={() => fileInputRef.current.click()}
                          disabled={uploading}
                          className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                        >
                          <div className="bg-white p-2 sm:p-3 rounded-full text-rose-500 shadow-md">
                            {uploading ? (
                              <div className="animate-spin h-4 w-4 sm:h-5 sm:w-5 border-2 border-rose-500 rounded-full border-t-transparent"></div>
                            ) : (
                              <FaUpload className="text-base sm:text-lg" />
                            )}
                          </div>
                        </button>
                        <input 
                          type="file" 
                          ref={fileInputRef} 
                          onChange={handleLogoUpload} 
                          accept="image/*" 
                          className="hidden" 
                        />
                      </div>
                      <p className="mt-2 text-xs text-gray-500 text-center">
                        Recommended: 500x500px, JPG/PNG
                      </p>
                    </div>

                    {/* Form Fields */}
                    <div className="flex-grow space-y-4 sm:space-y-5">
                      <SettingsInput 
                        label="Restaurant Name" 
                        value={profile.name} 
                        onChange={e => handleFieldChange('name', e.target.value)}
                        placeholder="Your restaurant name"
                      />
                      <SettingsInput 
                        label="Address" 
                        value={profile.address} 
                        onChange={e => handleFieldChange('address', e.target.value)}
                        placeholder="Full business address"
                      />
                      <SettingsInput 
                        label="Phone Number" 
                        value={profile.phone} 
                        onChange={e => handleFieldChange('phone', e.target.value)}
                        placeholder="Contact number"
                      />
                    </div>
                  </div>

                  <div className="mt-6 sm:mt-8 pt-4 sm:pt-6 border-t border-gray-100 flex justify-end">
                    <button 
                      onClick={handleSave} 
                      disabled={saving}
                      className="bg-gradient-to-r from-rose-500 to-amber-500 text-white px-4 sm:px-6 py-2 sm:py-3 rounded-lg font-medium hover:opacity-90 transition-opacity shadow-md flex items-center space-x-2 disabled:opacity-70 text-sm sm:text-base"
                    >
                      {saving ? (
                        <>
                          <div className="animate-spin h-4 w-4 sm:h-5 sm:w-5 border-2 border-white rounded-full border-t-transparent"></div>
                          <span>Saving...</span>
                        </>
                      ) : (
                        <>
                          <FaSave />
                          <span>Save Changes</span>
                        </>
                      )}
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <h2 className="text-lg sm:text-xl font-semibold text-gray-800 mb-4 sm:mb-6">Account Security</h2>
                  <form className="space-y-4 sm:space-y-5" onSubmit={handlePasswordUpdate} autoComplete="off">
                    <PasswordInput 
                      label="Current Password" 
                      value={currentPassword} 
                      onChange={e => setCurrentPassword(e.target.value)} 
                      show={showCurrent} 
                      setShow={setShowCurrent}
                      placeholder="Enter current password"
                    />
                    <PasswordInput 
                      label="New Password" 
                      value={newPassword} 
                      onChange={e => setNewPassword(e.target.value)} 
                      show={showNew} 
                      setShow={setShowNew}
                      placeholder="Enter new password"
                    />
                    <PasswordInput 
                      label="Confirm New Password" 
                      value={confirmPassword} 
                      onChange={e => setConfirmPassword(e.target.value)} 
                      show={showConfirm} 
                      setShow={setShowConfirm}
                      placeholder="Confirm new password"
                    />
                    <div className="mt-8 pt-6 border-t border-gray-100 flex justify-end">
                      <button 
                        type="submit"
                        className="bg-gradient-to-r from-rose-500 to-amber-500 text-white px-6 py-3 rounded-lg font-medium hover:opacity-90 transition-opacity shadow-md flex items-center space-x-2 disabled:opacity-70"
                        disabled={passwordLoading}
                      >
                        {passwordLoading ? (
                          <>
                            <div className="animate-spin h-5 w-5 border-2 border-white rounded-full border-t-transparent"></div>
                            <span>Updating...</span>
                          </>
                        ) : (
                          <span>Update Password</span>
                        )}
                      </button>
                    </div>
                    <AnimatePresence>
                      {passwordError && (
                        <motion.div
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: 20 }}
                          className="fixed bottom-6 right-6 bg-red-500 text-white px-6 py-3 rounded-lg shadow-lg flex items-center space-x-2"
                        >
                          <div className="w-5 h-5 bg-white rounded-full flex items-center justify-center text-red-500">!</div>
                          <span>{passwordError}</span>
                        </motion.div>
                      )}
                      {passwordSuccess && (
                        <motion.div
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: 20 }}
                          className="fixed bottom-6 right-6 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg flex items-center space-x-2"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                          </svg>
                          <span>{passwordSuccess}</span>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </form>
                </>
              )}
            </motion.div>
          </div>

          {/* Preview Card */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl shadow-sm p-6 sticky top-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Profile Preview</h3>
              <div className="flex flex-col items-center text-center">
                <div className="w-32 h-32 rounded-full overflow-hidden border-4 border-white shadow-md mb-4">
                  <img 
                    src={profile.logoUrl ? `${API_URL}${profile.logoUrl}` : 'https://via.placeholder.com/128?text=No+Logo'} 
                    alt="Preview" 
                    className="w-full h-full object-cover"
                  />
                </div>
                <h4 className="text-xl font-medium text-gray-800">
                  {profile.name || 'Your Restaurant'}
                </h4>
                <p className="text-gray-600 mt-1">
                  {profile.address || 'Address not set'}
                </p>
                <p className="text-gray-600">
                  {profile.phone || 'Phone not provided'}
                </p>
              </div>
              
            </div>
          </div>
        </div>
      </div>

      {/* Toast Notifications */}
      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            className="fixed bottom-6 right-6 bg-red-500 text-white px-6 py-3 rounded-lg shadow-lg flex items-center space-x-2"
          >
            <div className="w-5 h-5 bg-white rounded-full flex items-center justify-center text-red-500">
              !
            </div>
            <span>{error}</span>
          </motion.div>
        )}
        {success && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            className="fixed bottom-6 right-6 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg flex items-center space-x-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
            </svg>
            <span>{success}</span>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function SettingsInput({ label, value, onChange, type = 'text', placeholder = '' }) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1.5">{label}</label>
      <input 
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className="w-full px-3 sm:px-4 py-2 sm:py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rose-300 focus:border-transparent transition-all text-sm sm:text-base"
      />
    </div>
  );
}

// Password input with eye icon
function PasswordInput({ label, value, onChange, show, setShow, placeholder }) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1.5">{label}</label>
      <div className="relative">
        <input
          type={show ? 'text' : 'password'}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          className="w-full px-3 sm:px-4 py-2 sm:py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rose-300 focus:border-transparent transition-all pr-10 text-sm sm:text-base"
        />
        <button
          type="button"
          className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-rose-500 p-1"
          onClick={() => setShow(s => !s)}
          tabIndex={-1}
        >
          {show ? <FaEyeSlash className="text-sm sm:text-base" /> : <FaEye className="text-sm sm:text-base" />}
        </button>
      </div>
    </div>
  );
}